// Default icons with consistent styling
import React from "react";
import { AlertCircle, Euro, Layers, Tag } from "lucide-react";
import { cn } from "utils/utils";

export const STAT_ICONS = {
  CARDS: <Layers className="h-8 w-8 stroke-current" />,
  VALUE: <Euro className="h-8 w-8 stroke-current" />,
  TYPE: <Tag className="h-8 w-8 stroke-current" />,
  STATUS: <AlertCircle className="h-8 w-8 stroke-current" />,
};

export interface StatItemProps {
  figure?: React.ReactNode;
  title?: React.ReactNode;
  value?: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
}

// Static stat component to reduce code duplication
export const StatItem: React.FC<StatItemProps> = ({
  figure,
  title,
  value,
  description = null,
  className,
}) => (
  <div className={cn("group relative min-w-0 flex-1 basis-0 p-4", className)}>
    {figure && (
      <div className="text-primary/60 absolute right-4 top-4">{figure}</div>
    )}
    {title && (
      <div className="text-base-content/70 mb-1 text-sm font-medium">
        {title}
      </div>
    )}
    {(value || value === 0) && (
      <div className="text-base-content text-2xl font-bold tracking-tight">
        {value}
      </div>
    )}
    {description && (
      <div className="text-base-content/60 mt-1 break-words text-sm">
        {description}
      </div>
    )}
  </div>
);

interface SkeletonStatProps {
  hasFigure?: boolean;
  hasDescription?: boolean;
  variant?: "cards" | "value" | "type" | "status" | "default";
}

// Skeleton stat for loading state - recreated to exactly match StatItem structure
export const SkeletonStat: React.FC<SkeletonStatProps> = ({
  hasFigure = true,
  hasDescription = true,
  variant = "default",
}) => {
  // Customize dimensions based on variant
  const getDimensions = () => {
    switch (variant) {
      case "cards":
        return {
          title: "w-10", // "Cards" - short word
          value: "w-12", // Numbers like "1,234"
          description: "w-20", // "123 distinct"
        };
      case "value":
        return {
          title: "w-10", // "Value" - short word
          value: "w-16", // "€ 1,234.56" - longer format
          description: "w-0", // No description for value
        };
      case "type":
        return {
          title: "w-8", // "Type" - short word
          value: "w-24", // Could be longer like "Commander" or "Standard"
          description: "w-32", // Tooltip could be longer
        };
      case "status":
        return {
          title: "w-12", // Status labels can vary
          value: "w-20", // Status values can be medium length
          description: "w-28", // Status tooltips tend to be longer
        };
      default:
        return {
          title: "w-12",
          value: "w-16",
          description: "w-20",
        };
    }
  };

  // Get the correct icon based on variant
  const getIcon = () => {
    switch (variant) {
      case "cards":
        return STAT_ICONS.CARDS;
      case "value":
        return STAT_ICONS.VALUE;
      case "type":
        return STAT_ICONS.TYPE;
      case "status":
        return STAT_ICONS.STATUS;
      default:
        return <div className="skeleton h-8 w-8"></div>;
    }
  };

  const dimensions = getDimensions();

  return (
    <div className="group relative min-w-0 flex-1 basis-0 p-4">
      {hasFigure && (
        <div className="text-primary/60 absolute right-4 top-4">
          {getIcon()}
        </div>
      )}
      <div className="text-base-content/70 mb-1 text-sm font-medium">
        <div className={`skeleton h-5 ${dimensions.title}`}></div>
      </div>
      <div className="text-base-content text-2xl font-bold tracking-tight">
        <div className={`skeleton h-8 ${dimensions.value}`}></div>
      </div>
      {hasDescription && (
        <div className="text-base-content/60 mt-1 text-sm">
          <div className={`skeleton h-5 ${dimensions.description}`}></div>
        </div>
      )}
    </div>
  );
};
